/**
 * Challenge progress ring — overall completion rate, days done/left.
 * Only rendered when user is member AND challenge is active.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Flame, TrendingUp, CalendarCheck } from 'lucide-react'
import { differenceInDays, parseISO } from 'date-fns'

import type {
  ChallengeMembership,
  ChallengeDetail,
  ChallengeHabitProgress,
} from '@turnifly/shared/types'

// ── Props ────────────────────────────────────────────────────

interface ChallengeProgressSectionProps {
  membership: ChallengeMembership
  challenge: ChallengeDetail
  habitProgress: ChallengeHabitProgress[]
}

// ── Ring constants ───────────────────────────────────────────

const SIZE = 88
const SW = 7
const R = (SIZE - SW) / 2
const C = 2 * Math.PI * R

// ── Mini stat card ───────────────────────────────────────────

function MiniStat({ icon: Icon, color, value, label }: {
  icon: React.FC<{ size: number; style?: React.CSSProperties }>
  color: string
  value: string | number
  label: string
}): React.ReactElement {
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5 rounded-xl bg-white/[0.04] p-3">
      <div
        className="flex h-8 w-8 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={16} style={{ color }} />
      </div>
      <span className="mt-0.5 text-[15px] font-bold tabular-nums text-white">
        {value}
      </span>
      <span className="text-center text-[9px] leading-tight text-[var(--color-text-hint)]">
        {label}
      </span>
    </div>
  )
}

// ── Component ────────────────────────────────────────────────

export function ChallengeProgressSection({
  membership,
  challenge,
  habitProgress,
}: ChallengeProgressSectionProps): React.ReactElement {
  const { t } = useTranslation()

  const { overallRate, daysDone, totalDays, daysLeft } = useMemo(() => {
    const start = membership.personal_start_date ?? challenge.start_date
    const end = membership.personal_end_date ?? challenge.end_date

    const startDate = start ? parseISO(start) : new Date()
    const endDate = end
      ? parseISO(end)
      : new Date(startDate.getTime() + (challenge.duration_days - 1) * 86400000)
    const today = new Date()

    const total = Math.max(1, differenceInDays(endDate, startDate) + 1)
    const elapsed = Math.max(
      0,
      Math.min(total, differenceInDays(today, startDate) + 1),
    )
    const remaining = Math.max(0, total - elapsed)

    let rate = Number(membership.completion_rate) || 0
    if (habitProgress.length > 0) {
      const totalCompleted = habitProgress.reduce(
        (s, h) => s + h.completed_count,
        0,
      )
      const totalApplicable = habitProgress.reduce(
        (s, h) => s + h.total_applicable,
        0,
      )
      rate =
        totalApplicable > 0
          ? Math.round((totalCompleted / totalApplicable) * 100)
          : 0
    }

    return {
      overallRate: Math.min(rate, 100),
      daysDone: elapsed,
      totalDays: total,
      daysLeft: remaining,
    }
  }, [membership, challenge, habitProgress])

  const dashOffset = C - (overallRate / 100) * C
  const ringColor =
    overallRate >= 80
      ? '#22C55E'
      : overallRate >= 50
        ? '#3B82F6'
        : '#F59E0B'

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.15 }}
      className="mx-4 mb-3 rounded-2xl bg-white/[0.04] p-5"
    >
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-text-hint)]/50">
        {t('challenges.profile.overallProgress')}
      </p>

      <div className="flex items-center gap-5">
        {/* SVG ring */}
        <div className="relative shrink-0">
          <svg width={SIZE} height={SIZE} className="-rotate-90">
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              stroke="rgba(255,255,255,0.04)"
              strokeWidth={SW}
            />
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              stroke={ringColor}
              strokeWidth={SW}
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={dashOffset}
              className="transition-[stroke-dashoffset] duration-1000 ease-out"
              style={{ filter: `drop-shadow(0 0 6px ${ringColor}40)` }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[22px] font-bold tabular-nums text-white">
              {overallRate}%
            </span>
          </div>
        </div>

        {/* Mini stats */}
        <div className="flex flex-1 gap-2">
          <MiniStat
            icon={CalendarCheck}
            color="#3B82F6"
            value={`${daysDone}/${totalDays}`}
            label={t('challenges.days')}
          />
          <MiniStat
            icon={Flame}
            color="#F97316"
            value={daysLeft}
            label={t('challenges.profile.daysLeft', { count: daysLeft })}
          />
          <MiniStat
            icon={TrendingUp}
            color="#22C55E"
            value={`${overallRate}%`}
            label={t('challenges.profile.completionRate')}
          />
        </div>
      </div>
    </motion.div>
  )
}
